import React, { useState, useMemo, useEffect } from 'react';
import './DataTable.css';

export default function DataTable({ 
  data = [], 
  columns = [], 
  itemsPerPage = 10,
  searchableFields = [],
  onEdit = null,
  onDelete = null,
  onPasswordReset = null,
  onRowClick = null,
  title = '',
  expandableRowRenderer = null,
  customActions = null,
  serverSort = null,
  onServerSort = null,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSort, setSortConfig] = useState({ key: null, direction: 'asc' });
  const sortConfig = serverSort || localSort;
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filter data
  const filteredData = useMemo(() => {
    if (!searchTerm || searchableFields.length === 0) return data;
    
    return data.filter(row => {
      return searchableFields.some(field => {
        const value = row[field]?.toString().toLowerCase() || '';
        return value.includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, searchableFields]);

  // Sort data
  const sortedData = useMemo(() => {
    if (onServerSort || !sortConfig.key) return filteredData;
    
    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      
      // Compare values
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });
    
    return sorted;
  }, [filteredData, sortConfig, onServerSort]);

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  useEffect(() => {setCurrentPage(p => Math.min(p, Math.max(1, totalPages)));}, [totalPages]);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedData = sortedData.slice(startIdx, endIdx);

  // Handle sort
  const handleSort = (key) => {
    if (onServerSort) {onServerSort({key, direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'}); return;}
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  // Get sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Get cell value
  const getCellValue = (row, column) => {
    if (column.render) {
      return column.render(row[column.key], row);
    }
    const value = row[column.key];
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return value.toLocaleString();
    return value.toString();
  };

  return (
    <div className="data-table-container">
      {title && <h2 className="table-title">{title}</h2>}
      
      {/* Search Bar */}
      {searchableFields.length > 0 && (
        <div className="table-search">
          <input
            type="text"
            placeholder="Search records…"
            aria-label="Search records"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {expandableRowRenderer && <th className="expand-col"></th>}
              {columns.map(column => (
                <th
                  key={column.key}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                  className={column.sortable !== false ? 'sortable' : ''}
                  style={{ width: column.width }}
                  tabIndex={column.sortable !== false ? 0 : undefined}
                  onKeyDown={e=>{if(column.sortable!==false && ['Enter',' '].includes(e.key)){e.preventDefault();handleSort(column.key);}}}
                  aria-sort={sortConfig.key===column.key?(sortConfig.direction==='asc'?'ascending':'descending'):'none'}
                >
                  {column.label}
                  {column.sortable !== false && getSortIndicator(column.key)}
                </th>
              ))}
              {(onEdit || onDelete || onPasswordReset || customActions) && <th className="actions-header">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (expandableRowRenderer ? 1 : 0) + (onEdit || onDelete || onPasswordReset || customActions ? 1 : 0)} className="empty-state">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((row, idx) => (
                <React.Fragment key={row.id || idx}>
                  <tr className={`data-row ${expandedRows.has(row.id || idx) ? 'expanded' : ''}`}>
                    {expandableRowRenderer && (
                      <td className="expand-col">
                        {expandableRowRenderer && (
                          <button
                            className="expand-btn"
                            onClick={() => toggleRowExpansion(row.id || idx)}
                            title={expandedRows.has(row.id || idx) ? 'Hide details' : 'Show details'}
                          >
                            {expandedRows.has(row.id || idx) ? '▼' : '▶'}
                          </button>
                        )}
                      </td>
                    )}
                    {columns.map(column => (
                      <td key={column.key} className={column.className || ''}>
                        {getCellValue(row, column)}
                      </td>
                    ))}
                    {(onEdit || onDelete || onPasswordReset || customActions) && (
                      <td className="actions-cell">
                        {customActions ? (
                          customActions(row)
                        ) : (
                          <>
                            {onEdit && (
                              <button
                                className="btn-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row);
                                }}
                                title="Edit"
                              >
                                ✎
                              </button>
                            )}
                            {onPasswordReset && (
                              <button
                                className="btn-reset-password"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPasswordReset(row);
                                }}
                                title="Reset Password"
                              >
                                🔑
                              </button>
                            )}
                            {onDelete && (
                              <button
                                className="btn-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to delete this item?')) {
                                    onDelete(row);
                                  }
                                }}
                                title="Delete"
                              >
                                🗑
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                  {expandableRowRenderer && expandedRows.has(row.id || idx) && (
                    <tr className="details-row">
                      <td colSpan={columns.length + (expandableRowRenderer ? 1 : 0) + (onEdit || onDelete || onPasswordReset || customActions ? 1 : 0)}>
                        <div className="details-content">
                          {expandableRowRenderer(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="table-pagination">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            « First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ‹ Previous
          </button>

          {/* Page numbers */}
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Last »
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages} ({sortedData.length} total)
          </span>
        </div>
      )}
    </div>
  );
}
